import React, { useState } from 'react';
import { PlusIcon, FolderIcon, PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline';
import { NFSServer } from '../../types';

interface NFSShare {
  id: string;
  serverId: string;
  path: string;
  clients: string[];
}

interface SharesListProps {
  shares: NFSShare[];
  onAddShare: () => void;
  onEditShare: (id: string) => void;
  onDeleteShare: (id: string) => void;
  servers: NFSServer[];
}

const SharesList: React.FC<SharesListProps> = ({
  shares,
  onAddShare,
  onEditShare,
  onDeleteShare,
  servers
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  const filteredShares = shares.filter(share => {
    const serverObj = servers.find(s => s.id === share.serverId);
    const serverName = serverObj ? serverObj.name : '';
    return (
      serverName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      share.path.toLowerCase().includes(searchTerm.toLowerCase()) ||
      share.clients.join(',').toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  return (
    <div className="space-y-6">
      {/* Header with actions */}
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-zinc-100 flex items-center">
          <FolderIcon className="h-6 w-6 mr-2 text-indigo-500" />
          NFS Shares
        </h2>
        <button
          type="button"
          onClick={onAddShare}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none"
        >
          <PlusIcon className="h-4 w-4 mr-1.5" />
          New Share
        </button>
      </div>
      {/* Description */}
      <div className="bg-zinc-800/50 rounded-lg p-4 text-sm text-zinc-300">
        <p>Shares are exported directories from NFS servers. You can manage which clients have access to each share here.</p>
      </div>
      {/* Search Input */}
      <div className="relative">
        <input
          type="text"
          placeholder="Search shares..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full rounded-md bg-zinc-800 border border-zinc-700 px-4 py-2 text-zinc-200 focus:outline-none focus:border-indigo-500"
        />
      </div>
      {/* Shares list */}
      {shares.length === 0 ? (
        <div className="bg-zinc-800/30 rounded-lg border border-dashed border-zinc-700 p-8 text-center">
          <FolderIcon className="mx-auto h-12 w-12 text-zinc-500" />
          <h3 className="mt-2 text-sm font-medium text-zinc-400">No shares found</h3>
          <p className="mt-1 text-sm text-zinc-500">Get started by adding a new NFS share.</p>
          <div className="mt-6">
            <button
              type="button"
              onClick={onAddShare}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none"
            >
              <PlusIcon className="h-4 w-4 mr-1.5" />
              New Share
            </button>
          </div>
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-zinc-800 bg-zinc-900/30">
          <table className="min-w-full divide-y divide-zinc-800">
            <thead className="bg-zinc-800/50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">Server</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">Path</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">Clients</th>
                <th className="relative px-6 py-3"><span className="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800 bg-transparent">
              {filteredShares.map((share) => {
                const serverObj = servers.find(s => s.id === share.serverId);
                return (
                  <tr key={share.id} className="hover:bg-zinc-800/30 transition-colors duration-150">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-zinc-200">
                      <div className="flex items-center">
                        <FolderIcon className="h-5 w-5 mr-3 text-zinc-500" />
                        {serverObj ? serverObj.name : share.serverId}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-zinc-400">{share.path}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-zinc-400">{share.clients.join(', ')}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-3">
                        <button
                          type="button"
                          onClick={() => onEditShare(share.id)}
                          className="text-indigo-400 hover:text-indigo-300 transition-colors duration-150"
                          title="Edit Share"
                        >
                          <PencilSquareIcon className="h-5 w-5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => setConfirmDelete(share.id)}
                          className="text-red-400 hover:text-red-300 transition-colors duration-150"
                          title="Delete Share"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {/* Delete Confirmation Modal */}
      {confirmDelete && (
        <div className="fixed inset-0 flex items-center justify-center z-50">
          <div className="fixed inset-0 bg-zinc-950 bg-opacity-75 backdrop-blur-sm transition-opacity" onClick={() => setConfirmDelete(null)}></div>
          <div className="bg-zinc-900 rounded-xl shadow-2xl overflow-hidden w-full max-w-md relative z-10">
            <div className="p-6">
              <h3 className="text-lg font-medium text-zinc-100 mb-3">Confirm Delete</h3>
              <p className="text-sm text-zinc-300 mb-6">
                Are you sure you want to delete this share? This action cannot be undone.
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setConfirmDelete(null)}
                  className="px-4 py-2 rounded-md text-sm font-medium text-zinc-300 bg-zinc-800 hover:bg-zinc-700 focus:outline-none"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => { onDeleteShare(confirmDelete!); setConfirmDelete(null); }}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SharesList; 